import cx from 'classnames';
import { DisputeRemarkNote, Switch, hasSettledRemark, hasStandingRemark } from '@app/components/core';
import { TabBar } from '@app/components/core/tab-bar';
import { productTabPath } from '@app/constants/url';
import DayLog from '@app/components/pages/organizations/organization/clientReporting/sprintProduct/DayLog';
import RoleSummary from '@app/components/pages/organizations/organization/clientReporting/sprintProduct/RoleSummary';
import PricedSprintTotals from '@app/components/pages/organizations/organization/clientReporting/sprintProduct/PricedSprintTotals';
import {
  chaptersIn,
  offersFilter,
  peopleIn,
  reportsInChapters,
  reportsOf,
} from '@app/components/pages/organizations/organization/clientReporting/sprintProduct/reportFilter';
import {
  daysInSprintOrder,
  memberHoursTsv,
  rolesByHours,
  totalMinutesOf,
} from '@app/components/pages/organizations/organization/clientReporting/sprintProduct/sprintViews';
import RemoveReportModal from '@app/components/shared/removeReportModal';
import ReportModal from '@app/components/shared/reportModal';
import { Language } from '@app/constants/enums';
import { reportCardClass, tabClass } from '@app/constants/styles';
import { copyToClipboard, formatDateString, useToggleState } from '@app/utils';
import { useOrganizationId } from '@app/utils/hooks';
import {
  ReportEntity,
  SprintProductParamsType,
  SprintProductReportsParams,
  useCreatePmReport,
  useCreateSprintProduct,
  useListSprintProductClients,
  useListSprintProducts,
  useProduct,
  useRemovePmReport,
  useSendSprintProduct,
  useSprint,
  useSprintProductReports,
  useUpdatePmReport,
  useUpdateSprintProduct,
} from '@data';
import { transformSprintProductReportsData } from '@data/api/utils/helpers';
import { useOrganizationChapters } from '@data/hooks/organization';
import * as Sentry from '@sentry/browser';
import { Copy } from 'lucide-react';
import React, { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader } from '@ui';
import { Button } from '@ui/components/ui/button';
import { MultiSelectField } from '@ui/components/ui/multi-select';

import ProductHeader from './ProductHeader';
import Summary from './Summary';

const ProductReporting = (): ReactElement => {
  const organizationId = useOrganizationId() as string;
  const { sprintId, productId } = useParams() as Pick<SprintProductParamsType, 'sprintId' | 'productId'>;
  // Kept in for the same reason as the client's side: filtering weekends out of the day view would drop
  // a Saturday's reports from its total while the role view still counted them. Empty days go instead.
  const hideWeekend = false;
  const { data: sprint } = useSprint(sprintId);
  const { data: product } = useProduct(productId);
  const { data: sprintProducts, isLoading: isSprintProductsLoading } = useListSprintProducts({ sprintId, productId });
  const sprintProductReportParams: SprintProductReportsParams = {
    sprint,
    productId,
  };
  const { data: sprintProductReports } = useSprintProductReports(sprintProductReportParams);
  // includeDisabled: render labels for historical involvements pointing at chapters the admin
  // has since disabled. The picker (ReportModal) stays on the active-only default.
  const { data: chapters } = useOrganizationChapters(organizationId, { includeDisabled: true });
  const { mutate: createSprintProduct } = useCreateSprintProduct(organizationId, {}, { sprintId, productId });
  const { mutate: updateSprintProduct } = useUpdateSprintProduct(organizationId, {}, { sprintId, productId });
  const { mutate: sendSprint } = useSendSprintProduct();
  const { mutate: createReport, mutateAsync: createReportAsync } = useCreatePmReport(
    organizationId,
    sprintProductReportParams,
  );
  const { mutate: updateReport } = useUpdatePmReport(sprintProductReportParams);
  const { mutate: removeReport } = useRemovePmReport(sprintProductReportParams);
  const sprintProduct = sprintProducts?.items?.[0];
  const { data: sprintProductClients } = useListSprintProductClients(
    sprintProduct && !sprintProduct.mutation ? sprintProduct.id : undefined,
  );

  /*
   * Whose time the report is showing. The client's own view of the same sprint carries this too — see the
   * note on `reportFilter` — so both sides can read a sprint one person at a time.
   */
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  // Applied after the member filter, so the two narrow together rather than either replacing the other.
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const people = useMemo(() => peopleIn(sprintProductReports), [sprintProductReports]);
  const reportsInView = useMemo(
    () => reportsInChapters(reportsOf(sprintProductReports, selectedUserIds), selectedRoles),
    [sprintProductReports, selectedUserIds, selectedRoles],
  );

  const { detailed, brief } = React.useMemo(
    () => transformSprintProductReportsData(hideWeekend, sprint, reportsInView),
    [hideWeekend, sprint, reportsInView],
  );
  /*
   * The sprint's own hours, never the filter's. "Total hours" is what the sprint came to and what the
   * client approves against, so narrowing to one person must not appear to change it. The views below are
   * the filtered ones.
   */
  const { detailed: everyone, brief: everyoneRoles } = React.useMemo(
    () => transformSprintProductReportsData(hideWeekend, sprint, sprintProductReports),
    [hideWeekend, sprint, sprintProductReports],
  );

  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [view, setView] = useState<'role' | 'day'>('role');
  // Owned by the screen so it holds its position across a view switch, matching the client's side.
  const [expandAll, setExpandAll] = useState(false);
  const [isAddFormOpen, toggleAddForm] = useToggleState(false);
  const [workDayToAdd, setWorkDayToAdd] = React.useState<string | undefined>();
  const [selectedReport, setSelectedReport] = React.useState<ReportEntity | undefined>();
  const [reportToEdit, setReportToEdit] = React.useState<ReportEntity | undefined>();
  const [isDeleteFormOpen, toggleDeleteForm] = useToggleState(false);

  const roles = rolesByHours(brief);
  const days = daysInSprintOrder(detailed);
  const totalMinutes = totalMinutesOf(everyone);

  /**
   * The sprint's hours on the clipboard, for the spreadsheet the numbers are kept in. `everyoneRoles`
   * rather than the filtered `roles`: the member filter narrows what is on screen, and half a sprint
   * pasted into that sheet is a wrong number nobody would think to check.
   */
  const copyMemberHours = async () => {
    if (!sprint) return;
    try {
      await copyToClipboard(memberHoursTsv(sprint.number, everyoneRoles));
      toast.success(t('memberHoursCopied'));
    } catch (error) {
      // Never silent: a write that fails leaves whatever was on the clipboard before, and that is what
      // lands in the sheet.
      Sentry.captureException(error);
      toast.error(t('memberHoursCopyFailed'));
    }
  };

  const showAddForm = (workDay: string) => {
    toggleAddForm();
    setWorkDayToAdd(workDay);
  };
  const closeAddForm = () => {
    toggleAddForm();
    setWorkDayToAdd(undefined);
  };

  const showEditForm = (report: ReportEntity) => {
    setReportToEdit(report);
  };
  const closeEditForm = () => {
    setReportToEdit(undefined);
  };

  const showDeleteForm = (report: ReportEntity) => {
    setSelectedReport(report);
    toggleDeleteForm();
  };

  const closeDeleteForm = () => {
    toggleDeleteForm();
    setSelectedReport(undefined);
  };

  const handleDeleting = () => {
    if (selectedReport && removeReport) {
      removeReport(selectedReport?.id);
    }
    closeDeleteForm();
  };

  const handleCreateReport = (values: Partial<ReportEntity>) => {
    createReport(values);
    closeAddForm();
  };

  const handleUpdateReport = (values: Partial<ReportEntity>) => {
    updateReport(values);
    closeEditForm();
  };

  const isReportLoading = !detailed || !brief || !chapters;
  const isEmpty = !isReportLoading && days.length === 0;
  const resolveRoleLabel = (key: string): string => chapters?.items?.find(c => c.role === key)?.text ?? key;
  // Off the unfiltered read, so narrowing to one chapter does not leave the picker offering only that one.
  const chapterFilterOptions = useMemo(
    () => chaptersIn(sprintProductReports, resolveRoleLabel),
    [sprintProductReports, chapters],
  );

  // Decision 3: authoring stays on this screen. A manager who reads a sprint and sees something wrong
  // fixes it here, rather than being sent to the person's page to correct a number they are looking at.
  const authoring = { onAddForDay: showAddForm, onEditReport: showEditForm, onRemoveReport: showDeleteForm };

  const disabledDays = useMemo(() => {
    if (!sprint) return undefined;
    return { before: new Date(sprint.startDate), after: new Date(sprint.endDate) };
  }, [sprint]);

  return (
    <div className="w-full">
      {!sprint || !product || isSprintProductsLoading ? (
        <Loader />
      ) : (
        <>
          <ProductHeader
            product={product}
            sprintProduct={sprintProduct}
            sprint={sprint}
            createSprintProduct={createSprintProduct}
            updateSprintProduct={updateSprintProduct}
            sendSprint={sendSprint}
            sprintProductClients={sprintProductClients}
          />
          <div className={cx(reportCardClass, 'mb-10')}>
            {/* Stacked on a phone: the date needs its line, or it breaks a word to a row. */}
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <div className="whitespace-nowrap">
                <span className="text-muted-foreground mr-2">{t('date')}</span>{' '}
                <span className="font-bold text-foreground">
                  {`${formatDateString(sprint?.startDate, language as Language, 'dd MMM')} -
            ${formatDateString(sprint?.endDate, language as Language, 'dd MMM yyyy')}`}
                </span>
              </div>
              {/* Read-only: this page edits no money, so reading a sprint must not open a year's version. */}
              <PricedSprintTotals
                readOnly
                product={product}
                sprintId={sprint.id}
                sprintStart={sprint.startDate.slice(0, 10)}
                groups={everyoneRoles ?? []}
                totalMinutes={totalMinutes}
                costHref={`${productTabPath(organizationId, productId, 'costs')}?sprint=${sprintId}`}
              />
            </div>
            {/* The remark goes to the summary field's own row rather than under it, so what has to change is
                level with the thing to change. It stays there once it has been answered, plainly: what the
                client asked is what our answer has to be read against. */}
            <Summary
              sprintProduct={sprintProduct}
              note={
                sprintProduct && (hasStandingRemark(sprintProduct) || hasSettledRemark(sprintProduct)) ? (
                  <DisputeRemarkNote sprintProduct={sprintProduct} settled={hasSettledRemark(sprintProduct)} />
                ) : undefined
              }
            />
            {/* One row on a wide screen; on a phone the tabs keep a line and the controls go under them. */}
            <div className="mb-6 mt-6 flex flex-col gap-3 md:flex-row md:items-end md:gap-6 md:border-b md:border-border">
              <TabBar className="md:border-b-0" activeKey={view}>
                <button
                  type="button"
                  aria-pressed={view === 'role'}
                  onClick={() => setView('role')}
                  className={tabClass(view === 'role')}
                >
                  {t('byTeam')}
                </button>
                <button
                  type="button"
                  aria-pressed={view === 'day'}
                  onClick={() => setView('day')}
                  className={tabClass(view === 'day')}
                >
                  {t('byDay')}
                </button>
              </TabBar>
              {/* Beside the expand, on the tab row rather than in a filter bar of its own: it belongs to
                  both views of the report, the way the expand does. */}
              <div className="flex flex-wrap items-center gap-3 md:ml-auto md:flex-nowrap md:gap-4 md:pb-3">
                {offersFilter(chapterFilterOptions) && (
                  <MultiSelectField
                    label={<span className="sr-only">{t('chapters')}</span>}
                    options={chapterFilterOptions}
                    value={selectedRoles}
                    onChange={setSelectedRoles}
                    emptyLabel={t('allChapters')}
                    searchable={chapterFilterOptions.length > 6}
                    searchPlaceholder={t('searchChapter')}
                    emptySearchLabel={t('noMatches')}
                    renderCountSummary={count => t('nItemsSelected', { count })}
                    className="w-full gap-0 md:w-52"
                  />
                )}
                {offersFilter(people) && (
                  <MultiSelectField
                    label={<span className="sr-only">{t('titles.members')}</span>}
                    options={people}
                    value={selectedUserIds}
                    onChange={setSelectedUserIds}
                    emptyLabel={t('allMembers')}
                    searchable={people.length > 6}
                    searchPlaceholder={t('search')}
                    emptySearchLabel={t('noMatches')}
                    renderCountSummary={count => t('nItemsSelected', { count })}
                    className="w-full gap-0 md:w-52"
                  />
                )}
                <Switch value={expandAll} onChange={() => setExpandAll(value => !value)} text={t('titles.expandAll')} />
                <Button variant="outline" size="sm" onClick={copyMemberHours} disabled={isReportLoading || isEmpty}>
                  <Copy /> {t('copyMemberHours')}
                </Button>
              </div>
            </div>

            {isReportLoading && <Loader />}
            {isEmpty && <p className="text-sm text-muted-foreground">{t('noReportedTimeOnSprint')}</p>}
            {!isReportLoading &&
              !isEmpty &&
              (view === 'role' ? (
                <RoleSummary groups={roles} resolveLabel={resolveRoleLabel} expandAll={expandAll} />
              ) : (
                <DayLog days={days} chapters={chapters?.items} expandAll={expandAll} authoring={authoring} />
              ))}
          </div>

          {isAddFormOpen ? (
            <ReportModal
              title={t('titles.addEntry')}
              show={isAddFormOpen}
              onHide={closeAddForm}
              onSubmit={handleCreateReport}
              onSubmitIncident={async values => {
                await createReportAsync(values);
                closeAddForm();
              }}
              disabledDays={disabledDays}
              date={workDayToAdd}
              productId={product?.id}
            />
          ) : null}

          {reportToEdit ? (
            <ReportModal
              title={t('titles.editEntry')}
              show={!!reportToEdit}
              onHide={closeEditForm}
              initialData={reportToEdit}
              onSubmit={handleUpdateReport}
              disabledDays={disabledDays}
            />
          ) : null}

          <RemoveReportModal show={isDeleteFormOpen} onHide={closeDeleteForm} onRemove={handleDeleting} />
        </>
      )}
    </div>
  );
};

export default ProductReporting;
